import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Circle } from 'lucide-react'
import type { NtTerm } from '../../core/types'
import { useRoster } from '../admin/model'
import { useNorseTerm } from './NorseTermData'
import { useNorseTermTeachers, unplannedParts, ranksTotal } from './model'
import { teacherProgress, studentProgress, groupStudents, type StudentRow, type CourseStage } from './dashboard'
import { Empty } from '../../ui/States'

/**
 * Prep dashboard — for NorseTerm admins and office in the run-up to the term.
 *
 * Who still owes what. Teachers first, not-done at the top, because that is a
 * list somebody chases. Then students rolled up by grade or seminar, because
 * those are the levers an admin actually pulls — a grade-level reminder, a
 * word with one seminar teacher — and a click into any group for the names.
 *
 * Desktop-primary: it is tables. On a phone the same numbers stack as cards.
 */

const STAGE: Record<CourseStage, string> = {
  none: 'No course yet', draft: 'Draft', submitted: 'Submitted, waiting on review',
  returned: 'Sent back', approved: 'Approved',
}

/** `show` picks one half: staff (course completion) or students (selections and proposals). Both when omitted. */
export function Dashboard({ term, show }: { term: NtTerm; show?: 'staff' | 'students' }) {
  const { roles, courses, students: rankings, proposals, placement, schedules } = useNorseTerm()
  const teachers = useNorseTermTeachers(roles.review || roles.run)
  const { rows: roster } = useRoster(roles.review || roles.run)

  const teacherRows = useMemo(() =>
    teacherProgress(teachers, (courses ?? []).filter((c) => !c.assignedOnly), (c) => unplannedParts(c.plan ?? [])), [teachers, courses])
  const studentRows = useMemo(() =>
    studentProgress(roster ?? [], rankings ?? [], proposals, placement, ranksTotal(term), new Date(), schedules),
  [roster, rankings, proposals, placement, term, schedules])

  return (
    <div className="wide">
      {show !== 'students' && <TeachersPanel rows={teacherRows} />}
      {show !== 'staff' && <StudentsPanel rows={studentRows} canSeeRankings={roles.run || roles.review} placementRun={!!placement} />}
    </div>
  )
}

/** One checklist column in the table: an icon under its heading, so the course gets the room. */
function TickCell({ on, label }: { on: boolean; label: string }) {
  return (
    <td className="dash__cell">
      {on ? <CheckCircle2 size={18} className="dash__yes" aria-hidden="true" /> : <Circle size={18} className="dash__no" aria-hidden="true" />}
      <span className="visually-hidden">{label}: {on ? 'done' : 'not yet'}</span>
    </td>
  )
}

function Tick({ on, label }: { on: boolean; label: string }) {
  return on
    ? <span className="dash__tick is-on"><CheckCircle2 size={14} aria-hidden="true" /> {label}</span>
    : <span className="dash__tick"><Circle size={14} aria-hidden="true" /> {label}</span>
}

function TeachersPanel({ rows }: { rows: ReturnType<typeof teacherProgress> }) {
  const [onlyOpen, setOnlyOpen] = useState(true)
  const shown = onlyOpen ? rows.filter((r) => r.done < 4) : rows
  const done = rows.filter((r) => r.done === 4).length
  return (
    <section style={{ marginTop: 'var(--s-3)' }}>
      <div className="pagehead">
        <h2 className="work__heading" style={{ margin: 0 }}>
          Teachers · {done} of {rows.length} done
        </h2>
        <button className="chip" onClick={() => setOnlyOpen((v) => !v)}>{onlyOpen ? 'Show everyone' : 'Not done only'}</button>
      </div>
      <div className="meter" aria-hidden="true"><span style={{ width: `${rows.length ? (done / rows.length) * 100 : 0}%` }} /></div>

      {shown.length === 0 ? (
        <Empty title={rows.length ? 'Every teacher is done' : 'No teachers yet'}
          body={rows.length ? 'Course overview, three days planned, sent for review and approved: all of them.' : 'Give people the NorseTerm Teacher role in Accounts.'} />
      ) : (
        <>
          <table className="table">
            <thead><tr>
              <th>Teacher</th><th className="dash__course">Course</th><th>Where it is</th>
              <th className="dash__cell">Overview</th><th className="dash__cell">Planned</th>
              <th className="dash__cell">Sent</th><th className="dash__cell">Approved</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.email || r.name}>
                  <td><div style={{ fontWeight: 610 }}>{r.name}</div><div className="work__sub">{r.email}</div></td>
                  <td className="dash__course">{r.course ? r.course.title || 'Untitled course' : '—'}</td>
                  <td>
                    {STAGE[r.stage]}
                    {r.course && r.unplanned > 0 && <div className="work__sub">{r.unplanned} of 6 parts not planned</div>}
                  </td>
                  <TickCell on={r.artifacts} label="Course overview" />
                  <TickCell on={!!r.course && r.unplanned === 0} label="Three days planned" />
                  <TickCell on={r.stage !== 'none' && r.stage !== 'draft'} label="Sent for review" />
                  <TickCell on={r.stage === 'approved'} label="Approved" />
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.email || r.name} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{r.name}</div>
                  <div className="work__sub">{r.course?.title ?? 'No course yet'} · {STAGE[r.stage]}</div>
                  <div className="dash__ticks" style={{ marginTop: 4 }}>
                    <Tick on={r.artifacts} label="Course overview" />
                    <Tick on={!!r.course && r.unplanned === 0} label="Three days planned" />
                    <Tick on={r.stage !== 'none' && r.stage !== 'draft'} label="Sent for review" />
                    <Tick on={r.stage === 'approved'} label="Approved" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {rows.some((r) => r.stage === 'submitted') && (
        <p className="opt__hint">Waiting on review? <Link to="/norseterm/review">Open course review</Link>.</p>
      )}
    </section>
  )
}

const PROJECT_WORDS: Record<StudentRow['project'], string> = {
  none: '—', draft: 'Drafting', submitted: 'With seminar teacher', checked: 'Ready for approval',
  returned: 'Sent back', approved: 'Approved',
}
const PLACED_WORDS: Record<StudentRow['placed'], string> = {
  both: 'Placed', partial: 'Half placed', none: 'Not placed', independent: 'Independent project', 'not run': '—',
}

function StudentsPanel({ rows, canSeeRankings, placementRun }: {
  rows: StudentRow[]
  canSeeRankings: boolean
  placementRun: boolean
}) {
  const [by, setBy] = useState<'grade' | 'seminar'>('grade')
  const [open, setOpen] = useState<string | null>(null)
  const [show, setShow] = useState<'all' | 'unranked' | 'project' | 'unplaced' | 'noartifact'>('all')
  const groups = useMemo(() => groupStudents(rows, by), [rows, by])
  const keyOf = (r: StudentRow) => (by === 'grade' ? `Grade ${r.grade}` : r.seminar || 'No seminar')

  const inGroup = rows
    .filter((r) => open && keyOf(r) === open)
    .filter((r) => show === 'all' || (show === 'unranked' && !r.ranked) || (show === 'project' && r.project !== 'none')
      || (show === 'unplaced' && (r.placed === 'none' || r.placed === 'partial'))
      || (show === 'noartifact' && r.artifact === 'not chosen'))
    .sort((a, b) => a.name.localeCompare(b.name))

  const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '—')

  return (
    <section style={{ marginTop: 'var(--s-6)' }}>
      <div className="pagehead">
        <h2 className="work__heading" style={{ margin: 0 }}>Students · {rows.length} on the student list</h2>
        <div className="chips" role="tablist" aria-label="Group students by">
          {(['grade', 'seminar'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={by === k} className={`chip${by === k ? ' is-on' : ''}`}
              onClick={() => { setBy(k); setOpen(null) }}>By {k}</button>
          ))}
        </div>
      </div>
      {!canSeeRankings && (
        <p className="opt__hint">Course selections and placement are visible to whoever runs placement; this shows independent project proposals only.</p>
      )}

      {rows.length === 0 ? (
        <Empty title="No students yet" body="Upload the student list from JMC (Manage → Student list). Progress shows here once it is in." />
      ) : (
        <>
          <table className="table">
            <thead><tr>
              <th>{by === 'grade' ? 'Grade' : 'Seminar'}</th><th className="num">Students</th>
              {canSeeRankings && <th className="num">Selected courses</th>}
              <th className="num">Projects in progress</th><th className="num">Projects approved</th>
              {canSeeRankings && placementRun && <th className="num">Placed</th>}
              {placementRun && <th className="num">Artifact chosen</th>}
            </tr></thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.key} className={open === g.key ? 'is-open' : ''}>
                  <td><button className="row-link" aria-expanded={open === g.key}
                    onClick={() => setOpen(open === g.key ? null : g.key)}>{g.key}</button></td>
                  <td className="num">{g.students}</td>
                  {canSeeRankings && <td className="num">{g.ranked} <span className="work__sub">({pct(g.ranked, g.students)})</span></td>}
                  <td className="num">{g.projectsInProgress}</td>
                  <td className="num">{g.projectsApproved}</td>
                  {canSeeRankings && placementRun && <td className="num">{g.placed} <span className="work__sub">({pct(g.placed, g.students)})</span></td>}
                  {placementRun && <td className="num">{g.artifactChosen} <span className="work__sub">({pct(g.artifactChosen, g.students)})</span></td>}
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {groups.map((g) => (
              <li key={g.key} className="work__row">
                <div className="work__main">
                  <button className="work__title" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    onClick={() => setOpen(open === g.key ? null : g.key)}>{g.key}</button>
                  <div className="work__sub">
                    {g.students} students{canSeeRankings && ` · ${g.ranked} selected courses`} · {g.projectsApproved} projects approved
                    {canSeeRankings && placementRun && ` · ${g.placed} placed`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {open && (
        <div className="card" style={{ marginTop: 'var(--s-3)' }}>
          <div className="pagehead">
            <h3 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{open}</h3>
            <div className="chips">
              {([['all', 'Everyone'], ...(canSeeRankings ? [['unranked', 'No course selections']] : []), ['project', 'Has a project'],
                ...(canSeeRankings && placementRun ? [['unplaced', 'Not placed']] : []),
                ...(placementRun ? [['noartifact', 'No artifact course']] : [])] as Array<[typeof show, string]>).map(([k, l]) => (
                <button key={k} className={`chip${show === k ? ' is-on' : ''}`} onClick={() => setShow(k)}>{l}</button>
              ))}
            </div>
          </div>
          {inGroup.length === 0 ? <p className="opt__hint">Nobody here matches.</p> : (
            <ul className="dash__people">
              {inGroup.map((r) => (
                <li key={r.email}>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span className="work__sub">
                    {by === 'grade' ? r.seminar : `Grade ${r.grade}`}
                    {canSeeRankings && (r.ranked ? ' · courses selected' : ' · no course selections')}
                    {r.project !== 'none' && ` · project: ${PROJECT_WORDS[r.project]}`}
                    {canSeeRankings && placementRun && ` · ${PLACED_WORDS[r.placed]}`}
                    {r.artifact === 'AM' || r.artifact === 'PM' ? ` · artifact: ${r.artifact === 'AM' ? 'morning' : 'afternoon'}`
                      : r.artifact === 'not chosen' ? ' · no artifact course' : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
